(() => {
  if (window.__PW_CART_ENGINE_LOADED__) return;
  window.__PW_CART_ENGINE_LOADED__ = true;

  const sectionNames = {
    drawer: 'cart-drawer',
    main: 'main-cart'
  };

  let mutationQueue = Promise.resolve();

  const route = (path) => {
    const configuredRoot = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';
    const root = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
    return `${root}${String(path).replace(/^\/+/, '')}`;
  };

  const parseSection = (html) => {
    if (typeof html !== 'string' || html.trim() === '') return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const requestError = new Error(
        (data && (data.description || data.message)) || `Cart request failed (${response.status})`
      );
      requestError.status = response.status;
      requestError.data = data;
      throw requestError;
    }

    return data;
  };

  const setText = (element, value) => {
    if (!element || value === null || typeof value === 'undefined') return;
    const next = String(value).trim();
    if (element.textContent.trim() !== next) element.textContent = next;
  };

  const updateGlobalCartMeta = (cart, sourceDocument) => {
    const sourceCount = sourceDocument && sourceDocument.querySelector('[data-cart-count]');
    const sourceTotal = sourceDocument && sourceDocument.querySelector('[data-cart-total]');
    const itemCount = cart && Number.isFinite(Number(cart.item_count))
      ? Number(cart.item_count)
      : sourceCount
        ? Number.parseInt(sourceCount.textContent, 10)
        : null;
    const totalText = sourceTotal ? sourceTotal.textContent.trim() : null;

    if (itemCount !== null && Number.isFinite(itemCount)) {
      document.querySelectorAll('[data-header-cart-count], [data-cart-count], [data-cart-count-number]')
        .forEach((element) => setText(element, itemCount));
    }

    if (totalText !== null) {
      document.querySelectorAll('[data-header-cart-total], [data-cart-total], [data-cart-subtotal]')
        .forEach((element) => setText(element, totalText));
    }

    if (cart && Number.isFinite(Number(cart.total_price))) {
      document.querySelectorAll('[data-cart-total-cents]')
        .forEach((element) => setText(element, cart.total_price));
    }

    document.querySelectorAll('.cart__form').forEach((form) => {
      form.classList.toggle('cart--empty', itemCount === 0);
    });

    document.querySelectorAll('[data-js-site-cart-sidebar]').forEach((drawer) => {
      drawer.classList.toggle('cart-is-empty', itemCount === 0);
      if (itemCount !== null && Number.isFinite(itemCount)) {
        drawer.dataset.cartItems = String(itemCount);
      }
    });

    document.querySelectorAll('[data-cart-checkout-button]').forEach((button) => {
      const disabled = itemCount === 0;
      button.classList.toggle('disabled', disabled);
      if (disabled) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  };

  const initializeDrawer = (root) => {
    if (!root) return;

    const run = () => {
      if (window.PWCartDrawer && typeof window.PWCartDrawer.init === 'function') {
        window.PWCartDrawer.init(root);
      }
      document.dispatchEvent(new CustomEvent('pw:cart-drawer:rendered', {
        detail: { root }
      }));
    };

    if (window.PWCartDrawer && typeof window.PWCartDrawer.init === 'function') {
      run();
      return;
    }

    const source = root.dataset.controllerSrc;
    if (!source) {
      run();
      return;
    }

    const existing = [...document.scripts].find((script) => script.src === source);
    if (existing) {
      if (existing.dataset.pwLoaded === 'true') run();
      else existing.addEventListener('load', run, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = source;
    script.defer = true;
    script.dataset.pwDynamicDrawerController = 'true';
    script.addEventListener('load', () => {
      script.dataset.pwLoaded = 'true';
      run();
    }, { once: true });
    document.head.appendChild(script);
  };

  const activateExternalScripts = (scope) => {
    if (!scope) return;

    scope.querySelectorAll('script[src]').forEach((inertScript) => {
      const source = inertScript.src;
      if (!source) return;

      const alreadyActive = [...document.scripts].some((script) => {
        return script !== inertScript
          && script.src === source
          && script.dataset.pwInertSectionScript !== 'true';
      });

      inertScript.dataset.pwInertSectionScript = 'true';
      inertScript.remove();
      if (alreadyActive) return;

      const script = document.createElement('script');
      script.src = source;
      script.async = false;
      script.dataset.pwActivatedSectionScript = 'true';
      document.head.appendChild(script);
    });
  };

  const renderDrawer = (html, cart) => {
    const parsed = parseSection(html);
    if (!parsed) return null;

    const nextDrawer = parsed.querySelector('.pw-cart-drawer');
    if (!nextDrawer) return parsed;

    let currentDrawer = nextDrawer.id
      ? document.getElementById(nextDrawer.id)
      : document.querySelector('.pw-cart-drawer');

    if (!currentDrawer) {
      const host = document.querySelector('[data-js-site-cart-sidebar]');
      const nextSection = parsed.querySelector(`#shopify-section-${sectionNames.drawer}`);
      const currentSection = host && host.querySelector(`#shopify-section-${sectionNames.drawer}`);

      if (currentSection && nextSection) {
        currentSection.innerHTML = nextSection.innerHTML;
        activateExternalScripts(currentSection);
        currentDrawer = currentSection.querySelector('.pw-cart-drawer');
      } else if (host && nextSection) {
        const insertedSection = document.importNode(nextSection, true);
        host.appendChild(insertedSection);
        activateExternalScripts(insertedSection);
        currentDrawer = host.querySelector('.pw-cart-drawer');
      } else if (host) {
        const insertedDrawer = document.importNode(nextDrawer, true);
        host.appendChild(insertedDrawer);
        activateExternalScripts(insertedDrawer);
        currentDrawer = host.querySelector('.pw-cart-drawer');
      }

      if (!currentDrawer) return parsed;
      initializeDrawer(currentDrawer);
      updateGlobalCartMeta(cart, parsed);
      return parsed;
    }

    const currentBody = currentDrawer.querySelector('.sidebar__body');
    const nextBody = nextDrawer.querySelector('.sidebar__body');

    if (currentBody && nextBody) {
      const scrollTop = currentBody.scrollTop;
      const currentSmile = currentBody.querySelector('[data-pw-smile-loyalty]');
      const importedBody = document.importNode(nextBody, true);
      const importedSmile = importedBody.querySelector('[data-pw-smile-loyalty]');

      // Smile is stateful and may already contain loaded SDK data. Move that
      // exact node into the fresh server markup instead of recreating it.
      if (currentSmile && importedSmile) importedSmile.replaceWith(currentSmile);

      currentBody.replaceWith(importedBody);
      importedBody.scrollTop = scrollTop;
    } else {
      // Defensive fallback for themes that render an older drawer structure.
      const currentItems = currentDrawer.querySelector('.cart__items');
      const nextItems = nextDrawer.querySelector('.cart__items');
      if (currentItems && nextItems) currentItems.innerHTML = nextItems.innerHTML;

      const currentShipping = currentDrawer.querySelector('[data-pw-cart-shipping]');
      const nextShipping = nextDrawer.querySelector('[data-pw-cart-shipping]');
      if (currentShipping && nextShipping) {
        currentShipping.replaceWith(document.importNode(nextShipping, true));
      }

      const currentSummary = currentDrawer.querySelector('[data-pw-cart-summary]');
      const nextSummary = nextDrawer.querySelector('[data-pw-cart-summary]');
      if (currentSummary && nextSummary) {
        currentSummary.replaceWith(document.importNode(nextSummary, true));
      }
    }

    currentDrawer.querySelectorAll('.cart-view-button').forEach((duplicate) => duplicate.remove());

    const currentHolder = currentDrawer.querySelector('.cart-holder');
    const nextHolder = nextDrawer.querySelector('.cart-holder');
    if (currentHolder && nextHolder) {
      currentHolder.dataset.items = nextHolder.dataset.items || '0';
    }

    initializeDrawer(currentDrawer);
    currentDrawer.classList.remove('pw-cart-needs-repair');
    updateGlobalCartMeta(cart, parsed);
    return parsed;
  };

  const renderMainCart = (html, cart) => {
    const parsed = parseSection(html);
    if (!parsed) return null;

    const currentMain = document.querySelector('#main-cart');
    const nextMain = parsed.querySelector('#main-cart') || parsed.body;
    if (!currentMain || !nextMain) return parsed;

    const currentItems = currentMain.querySelector('.cart__items');
    const nextItems = nextMain.querySelector('.cart__items');
    if (currentItems && nextItems) currentItems.innerHTML = nextItems.innerHTML;

    const currentSubtotal = currentMain.querySelector('[data-cart-subtotal]');
    const nextSubtotal = nextMain.querySelector('[data-cart-subtotal]');
    if (currentSubtotal && nextSubtotal) currentSubtotal.innerHTML = nextSubtotal.innerHTML;

    updateGlobalCartMeta(cart, parsed);
    return parsed;
  };

  const renderSections = (sections, cart) => {
    if (!sections || typeof sections !== 'object') {
      updateGlobalCartMeta(cart, null);
      return;
    }

    const drawerHTML = sections[sectionNames.drawer];
    const mainHTML = sections[sectionNames.main];
    const sourceDocument = drawerHTML
      ? renderDrawer(drawerHTML, cart)
      : mainHTML
        ? renderMainCart(mainHTML, cart)
        : null;

    if (drawerHTML && mainHTML) renderMainCart(mainHTML, cart);
    updateGlobalCartMeta(cart, sourceDocument);
  };

  const emitUpdated = (cart, source = 'cart') => {
    const detail = { cart, source };
    document.dispatchEvent(new CustomEvent('pw:cart:updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart-updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  };

  const enqueue = (task) => {
    const run = mutationQueue.then(task, task);
    mutationQueue = run.catch(() => undefined);
    return run;
  };

  const fetchCart = () => requestJSON(route('cart.js'), { cache: 'no-store' });

  const refresh = ({ openDrawer = false } = {}) => enqueue(async () => {
    const sectionQuery = encodeURIComponent(`${sectionNames.drawer},${sectionNames.main}`);
    const [cart, sectionsResponse] = await Promise.all([
      fetchCart(),
      fetch(`${route('')}?sections=${sectionQuery}`, {
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Section request failed (${response.status})`);
        return response.json();
      })
    ]);

    renderSections(sectionsResponse, cart);
    emitUpdated(cart, 'refresh');

    const recommendations = document.querySelector('cart-recommendations');
    if (recommendations && typeof recommendations.generateRecommendations === 'function') {
      recommendations.replaceChildren();
      recommendations.generateRecommendations();
    }

    if (openDrawer) {
      const sidebar = document.querySelector('[data-js-site-cart-sidebar]');
      if (sidebar && typeof sidebar.show === 'function') sidebar.show();
    }

    return cart;
  });

  window.PWCart = Object.assign(window.PWCart || {}, {
    sectionNames,
    route,
    requestJSON,
    fetchCart,
    enqueue,
    renderSections,
    updateGlobalCartMeta,
    emitUpdated,
    refresh
  });

  window.refreshCart = (options) => window.PWCart.refresh({
    openDrawer: !options || options.openDrawer !== false
  });

  let legacyRepairTimer = null;
  let legacyRepairInFlight = false;

  const drawerNeedsRepair = (drawer) => {
    if (!drawer) return false;

    const summary = drawer.querySelector('[data-pw-cart-summary]');
    const hasCanonicalSummary = summary
      && summary.querySelector('.cart__summary-row--subtotal')
      && summary.querySelector('.cart-summary__view-button');

    if (!hasCanonicalSummary) return true;

    const holder = drawer.querySelector('.cart-holder');
    const hasItems = Number.parseInt(holder && holder.dataset.items, 10) > 0;
    const shippingText = drawer.querySelector('[data-pw-cart-shipping] .cart-notice__text');
    const normalizedShippingText = shippingText
      ? shippingText.textContent.replace(/\s+/g, ' ').trim()
      : '';

    return hasItems && /^(?:(?:€|eur)\s*)?0(?:[,.]00)?$/i.test(normalizedShippingText);
  };

  const repairLegacyDrawer = () => {
    if (legacyRepairInFlight || typeof window.fetch !== 'function') return;

    const drawer = document.querySelector('.pw-cart-drawer');
    if (!drawer || !drawerNeedsRepair(drawer)) {
      if (drawer) drawer.classList.remove('pw-cart-needs-repair');
      return;
    }

    drawer.classList.add('pw-cart-needs-repair');
    legacyRepairInFlight = true;

    window.PWCart.refresh({ openDrawer: false })
      .catch((error) => {
        console.warn('Cart drawer: legacy update repair failed', error);
      })
      .finally(() => {
        legacyRepairInFlight = false;
        const latestDrawer = document.querySelector('.pw-cart-drawer');
        if (latestDrawer && drawerNeedsRepair(latestDrawer)) {
          window.clearTimeout(legacyRepairTimer);
          legacyRepairTimer = window.setTimeout(repairLegacyDrawer, 1200);
        }
      });
  };

  const scheduleLegacyDrawerRepair = () => {
    const drawer = document.querySelector('.pw-cart-drawer');
    if (!drawer || !drawerNeedsRepair(drawer)) return;

    drawer.classList.add('pw-cart-needs-repair');
    window.clearTimeout(legacyRepairTimer);
    legacyRepairTimer = window.setTimeout(repairLegacyDrawer, 0);
  };

  const observeLegacyDrawerUpdates = () => {
    const host = document.querySelector('[data-js-site-cart-sidebar]');
    if (!host || host.dataset.pwCartRepairObserver === 'true') return;

    host.dataset.pwCartRepairObserver = 'true';
    const observer = new MutationObserver(scheduleLegacyDrawerRepair);
    observer.observe(host, { childList: true, characterData: true, subtree: true });
    scheduleLegacyDrawerRepair();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeLegacyDrawerUpdates, { once: true });
  } else {
    observeLegacyDrawerUpdates();
  }

  document.addEventListener('pw:cart-drawer:rendered', observeLegacyDrawerUpdates);
  document.addEventListener('cart-updated', scheduleLegacyDrawerRepair);
  document.addEventListener('cart:updated', scheduleLegacyDrawerRepair);

  class PWCartForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pwCartFormReady === 'true') return;
      this.dataset.pwCartFormReady = 'true';
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.onSubmit = this.handleSubmit.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);

      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      delete this.dataset.pwCartFormReady;
    }

    handleSubmit(event) {
      event.preventDefault();
      const activeInput = this.querySelector('input.qty:focus');
      if (activeInput) this.commitInput(activeInput);
    }

    handleClick(event) {
      const remove = event.target.closest('.remove');
      if (!remove || !this.contains(remove)) return;
      event.preventDefault();
      const item = remove.closest('[data-js-cart-item]');
      if (item) this.updateCartQty(item, 0, { allowRemove: true });
    }

    handleChange(event) {
      const input = event.target.closest('input.qty');
      if (!input || !this.contains(input)) return;
      this.commitInput(input);
    }

    handleKeydown(event) {
      const input = event.target.closest('input.qty');
      if (!input || event.key !== 'Enter') return;
      event.preventDefault();
      this.commitInput(input);
    }

    commitInput(input) {
      const item = input.closest('[data-js-cart-item]');
      if (!item) return;
      // Queued rather than dropped: see the note in updateCartQty.
      this.updateCartQty(item, input.value, { allowRemove: false });
    }

    normalizeQuantity(item, requested, allowRemove) {
      const input = item.querySelector('input.qty');
      const step = Math.max(1, Number.parseInt(
        (input && input.dataset.quantityStep)
          || item.dataset.quantityStep
          || item.dataset.stepRule
          || (input && input.getAttribute('step'))
          || '1',
        10
      ) || 1);
      const minimum = step > 1 ? step : 1;
      let quantity = Number.parseInt(requested, 10);

      if (allowRemove && quantity === 0) return 0;
      if (!Number.isFinite(quantity)) quantity = minimum;
      quantity = Math.max(minimum, quantity);

      if (step > 1 && quantity % step !== 0) {
        quantity = Math.ceil(quantity / step) * step;
      }

      return quantity;
    }

    updateCartQty(item, requestedQuantity, { allowRemove = false } = {}) {
      if (!item) return Promise.resolve(null);

      // Do NOT drop the click while a previous change is in flight. PWCart.enqueue
      // already serialises mutations, so rapid clicks can safely queue. Returning
      // early here silently discarded every click made during the request +
      // re-render window, leaving the cart on a higher quantity than the one the
      // shopper clicked to (and the free shipping notice correct for that cart,
      // which reads as a stale notice).
      const input = item.querySelector('input.qty');
      const previousQuantity = input ? input.value : '';
      const quantity = this.normalizeQuantity(item, requestedQuantity, allowRemove);
      if (input) input.value = String(quantity);

      item.dataset.updating = 'true';
      this.form.classList.add('processing');
      this.clearAlert();

      return window.PWCart.enqueue(async () => {
        const payload = {
          quantity,
          sections: [sectionNames.drawer, sectionNames.main],
          sections_url: window.location.pathname
        };

        if (item.dataset.id) payload.id = item.dataset.id;
        else payload.line = Number.parseInt(item.dataset.line, 10);

        const cart = await requestJSON(route('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        renderSections(cart.sections, cart);
        emitUpdated(cart, quantity === 0 ? 'remove' : 'quantity');
        return cart;
      }).catch((error) => {
        if (input && document.contains(input)) input.value = previousQuantity;
        this.showAlert(error.message || this.defaultErrorMessage());
        return null;
      }).finally(() => {
        delete item.dataset.updating;
        if (this.form) this.form.classList.remove('processing');
      });
    }

    clearAlert() {
      const alert = this.querySelector('.alert[data-pw-cart-alert]');
      if (alert) alert.remove();
    }

    showAlert(message) {
      if (!this.form) return;
      this.clearAlert();
      const alert = document.createElement('span');
      alert.className = 'alert alert--error';
      alert.dataset.pwCartAlert = 'true';
      alert.setAttribute('role', 'alert');
      alert.textContent = message;
      this.form.prepend(alert);
    }

    defaultErrorMessage() {
      return window.KROWN && window.KROWN.settings && window.KROWN.settings.locales
        ? window.KROWN.settings.locales.cart_general_error
        : 'De winkelwagen kon niet worden bijgewerkt. Probeer het opnieuw.';
    }
  }

  if (!customElements.get('cart-form')) {
    customElements.define('cart-form', PWCartForm);
  }

  class PWCartProductQuantity extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pwQuantityReady === 'true') return;
      this.dataset.pwQuantityReady = 'true';
      this.input = this.querySelector('input.qty');
      this.minus = this.querySelector('.qty-minus');
      this.plus = this.querySelector('.qty-plus');
      this.onMinus = (event) => this.change(event, -1);
      this.onPlus = (event) => this.change(event, 1);

      if (this.minus) this.minus.addEventListener('click', this.onMinus, true);
      if (this.plus) this.plus.addEventListener('click', this.onPlus, true);
      this.updateButtons();
    }

    disconnectedCallback() {
      if (this.minus) this.minus.removeEventListener('click', this.onMinus, true);
      if (this.plus) this.plus.removeEventListener('click', this.onPlus, true);
      delete this.dataset.pwQuantityReady;
    }

    getRules() {
      const item = this.closest('[data-js-cart-item]');
      const step = Math.max(1, Number.parseInt(
        (this.input && this.input.dataset.quantityStep)
          || (item && item.dataset.quantityStep)
          || (item && item.dataset.stepRule)
          || (this.input && this.input.getAttribute('step'))
          || '1',
        10
      ) || 1);
      const declaredMin = Number.parseInt(this.input && this.input.min, 10);
      const minimum = step > 1
        ? Math.max(step, Number.isFinite(declaredMin) ? declaredMin : step)
        : 0;
      const declaredMax = Number.parseInt(this.input && this.input.max, 10);
      return {
        step,
        minimum,
        maximum: Number.isFinite(declaredMax) ? declaredMax : Infinity
      };
    }

    change(event, direction) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (!this.input || event.currentTarget.disabled) return;

      const item = this.closest('[data-js-cart-item]');
      const cartForm = this.closest('cart-form');
      if (!item || !cartForm || typeof cartForm.updateCartQty !== 'function') return;

      const rules = this.getRules();
      let current = Number.parseInt(this.input.value, 10);
      if (!Number.isFinite(current)) current = rules.step > 1 ? rules.minimum : 1;

      let next;
      if (rules.step > 1 && current % rules.step !== 0) {
        next = direction > 0
          ? Math.ceil(current / rules.step) * rules.step
          : Math.max(rules.minimum, Math.floor(current / rules.step) * rules.step);
      } else {
        next = current + (direction * rules.step);
      }

      next = Math.max(rules.minimum, Math.min(rules.maximum, next));
      if (next === current) return;

      this.input.value = String(next);
      this.updateButtons(next);
      cartForm.updateCartQty(item, next, { allowRemove: next === 0 });
    }

    updateButtons(value) {
      if (!this.input) return;
      const rules = this.getRules();
      const current = Number.parseInt(
        typeof value === 'undefined' ? this.input.value : value,
        10
      );
      if (!Number.isFinite(current)) return;

      if (this.minus) {
        const disabled = rules.step > 1 && current <= rules.minimum;
        this.minus.disabled = disabled;
        this.minus.classList.toggle('disabled', disabled);
      }

      if (this.plus) {
        const disabled = current + rules.step > rules.maximum;
        this.plus.disabled = disabled;
        this.plus.classList.toggle('disabled', disabled);
      }
    }
  }

  if (!customElements.get('cart-product-quantity')) {
    customElements.define('cart-product-quantity', PWCartProductQuantity);
  }

  class PWCartNote extends HTMLElement {
    constructor() {
      super();
      this.inflightController = null;
    }

    save(value) {
      if (this.inflightController) this.inflightController.abort();
      this.inflightController = new AbortController();
      const controller = this.inflightController;

      return requestJSON(route('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: value }),
        signal: controller.signal,
        keepalive: true
      }).catch((error) => {
        if (error && error.name === 'AbortError') return null;
        throw error;
      }).finally(() => {
        if (this.inflightController === controller) this.inflightController = null;
      });
    }

    flushPendingSave() {
      const textarea = this.querySelector('textarea');
      return textarea ? this.save(textarea.value) : Promise.resolve(null);
    }
  }

  if (!customElements.get('cart-note')) {
    customElements.define('cart-note', PWCartNote);
  }
})();
